import React, { useState } from "react";
import { toast } from "react-toastify";

import { DOCUMENT_FILE_MAX_BYTES, DOCUMENT_FILE_TYPES } from "../api/documentsApi";
import {
  CHAINABLE_TYPES,
  createLeaveApi,
  CreateLeavePayload,
  DpLeave,
  DpLeaveDetail,
  DpLeavePayer,
  DpLeaveType,
  LEAVE_PAYER_LABELS,
  LEAVE_TYPE_HINTS,
  LEAVE_TYPE_LABELS,
  updateLeaveApi,
  uploadLeaveFileApi,
} from "../api/leavesApi";
import { formatDateOnly, toDateInputValue } from "../utils/format";
import RuleMessages from "./RuleMessages";

type Props = {
  token: string;
  employeeId: string;
  /** Só o papel DP vê e envia CID, médico, atestado e CAT (ADR 0006). O backend garante. */
  isDp: boolean;
  /** Afastamentos anteriores do colaborador, para marcar continuação pela mesma causa. */
  previousLeaves?: DpLeave[];
  /** Edição: tipo e início ficam fixos (para mudar, cancele e lance de novo). */
  editing?: DpLeaveDetail | null;
  onClose: () => void;
  onSaved: (leaveId: string) => void;
};

const splitRules = (message?: string) => String(message ?? "").split(" | ").filter(Boolean);

function FileField({
  id,
  label,
  file,
  onChange,
  disabled,
  hint,
}: {
  id: string;
  label: string;
  file: File | null;
  onChange: (file: File | null) => void;
  disabled: boolean;
  hint: string;
}) {
  return (
    <div className="dpFormGroup">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="file"
        className="dpInput"
        accept={DOCUMENT_FILE_TYPES.join(",")}
        disabled={disabled}
        onChange={(e) => {
          const selected = e.target.files?.[0];
          if (!selected) return onChange(null);
          if (!DOCUMENT_FILE_TYPES.includes(selected.type)) return toast.error("Envie PDF, JPG, PNG ou WEBP.");
          if (selected.size > DOCUMENT_FILE_MAX_BYTES) return toast.error("O arquivo pode ter no máximo 10 MB.");
          onChange(selected);
        }}
      />
      <span className="dpFormHint">{file ? file.name : hint}</span>
    </div>
  );
}

export default function LeaveFormModal({ token, employeeId, isDp, previousLeaves = [], editing, onClose, onSaved }: Props) {
  const [form, setForm] = useState({
    type: (editing?.type ?? "") as DpLeaveType | "",
    startDate: toDateInputValue(editing?.startDate),
    endDate: toDateInputValue(editing?.eventEndDate ?? editing?.endDate),
    days: "",
    expectedEndDate: toDateInputValue(editing?.expectedEndDate),
    payer: "NENHUM" as DpLeavePayer,
    parentLeaveId: "",
    notes: editing?.notes ?? "",
    catNumber: editing?.catNumber ?? "",
    catIssuedAt: toDateInputValue(editing?.catIssuedAt),
    cid: "",
    physicianName: "",
    physicianCrm: "",
  });
  const [attachment, setAttachment] = useState<File | null>(null);
  const [cat, setCat] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const type = form.type || null;
  const isSickness = type === "ATESTADO_MEDICO" || type === "ACIDENTE_TRABALHO";
  const chainCandidates = previousLeaves.filter(
    (l) => CHAINABLE_TYPES.includes(l.type) && !l.isAutoContinuation && l.status !== "CANCELADO" && l.id !== editing?.id
  );

  const submit = async () => {
    if (!type) return toast.error("Escolha o tipo de afastamento.");
    if (!form.startDate) return toast.error("Informe o início.");

    setSaving(true);
    setErrors([]);
    try {
      // Arquivos primeiro: o lançamento recebe o assetId (e nunca o devolve).
      const uploadedAttachment = isDp && attachment ? await uploadLeaveFileApi(token, attachment) : null;
      const uploadedCat = isDp && cat ? await uploadLeaveFileApi(token, cat) : null;

      const sensitive: Partial<CreateLeavePayload> = isDp
        ? {
            ...(form.cid.trim() ? { cid: form.cid.trim() } : {}),
            ...(form.physicianName.trim() ? { physicianName: form.physicianName.trim() } : {}),
            ...(form.physicianCrm.trim() ? { physicianCrm: form.physicianCrm.trim() } : {}),
            ...(uploadedAttachment
              ? { attachmentAssetId: uploadedAttachment.assetId, attachmentFileName: uploadedAttachment.fileName ?? attachment?.name ?? null }
              : {}),
            ...(uploadedCat ? { catAssetId: uploadedCat.assetId, catFileName: uploadedCat.fileName ?? cat?.name ?? null } : {}),
          }
        : {};

      const common = {
        expectedEndDate: form.expectedEndDate || null,
        notes: form.notes.trim() || null,
        ...(type === "ACIDENTE_TRABALHO" ? { catNumber: form.catNumber.trim() || null, catIssuedAt: form.catIssuedAt || null } : {}),
        ...sensitive,
      };

      if (editing) {
        const { warnings } = await updateLeaveApi(token, editing.id, { ...common, endDate: form.endDate || null });
        toast.success("Afastamento atualizado.");
        warnings.forEach((w) => toast.warning(w));
        onSaved(editing.id);
      } else {
        const { leave, continuation, warnings } = await createLeaveApi(token, employeeId, {
          type,
          startDate: form.startDate,
          endDate: form.endDate || null,
          ...(form.days && !form.endDate ? { days: Number(form.days) } : {}),
          ...(type === "OUTRO" ? { payer: form.payer } : {}),
          ...(form.parentLeaveId ? { parentLeaveId: form.parentLeaveId } : {}),
          ...common,
        });
        toast.success(
          continuation
            ? `Afastamento lançado. A partir de ${formatDateOnly(continuation.startDate)} os dias são do INSS.`
            : "Afastamento lançado."
        );
        warnings.forEach((w) => toast.warning(w));
        onSaved(leave.id);
      }
    } catch (error: any) {
      setErrors(splitRules(error?.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide">
        <header className="dpModal__header">
          <h2 className="dpModal__title">{editing ? "Alterar afastamento" : "Novo afastamento"}</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>

        <div className="dpModal__body">
          <div className="dpFormGrid">
            <div className="dpFormGroup">
              <label htmlFor="leaveType">Tipo *</label>
              <select
                id="leaveType"
                className="dpSelect"
                value={form.type}
                disabled={saving || Boolean(editing)}
                onChange={(e) => set({ type: e.target.value as DpLeaveType, parentLeaveId: "" })}
              >
                <option value="">Selecione...</option>
                {(Object.keys(LEAVE_TYPE_LABELS) as DpLeaveType[]).map((t) => (
                  <option key={t} value={t}>
                    {LEAVE_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
              {type ? <span className="dpFormHint">{LEAVE_TYPE_HINTS[type]}</span> : null}
            </div>

            <div className="dpFormGroup">
              <label htmlFor="leaveStart">Início *</label>
              <input
                id="leaveStart"
                type="date"
                className="dpInput"
                value={form.startDate}
                disabled={saving || Boolean(editing)}
                onChange={(e) => set({ startDate: e.target.value })}
              />
            </div>

            <div className="dpFormGroup">
              <label htmlFor="leaveEnd">Último dia afastado</label>
              <input
                id="leaveEnd"
                type="date"
                className="dpInput"
                value={form.endDate}
                disabled={saving}
                onChange={(e) => set({ endDate: e.target.value })}
              />
              <span className="dpFormHint">
                {editing ? "Mudar o término redivide os dias entre empresa e INSS." : "Vazio = em aberto (ou o padrão do tipo)."}
              </span>
            </div>

            {!editing && !form.endDate ? (
              <div className="dpFormGroup">
                <label htmlFor="leaveDays">Ou quantidade de dias</label>
                <input
                  id="leaveDays"
                  type="number"
                  min={1}
                  className="dpInput"
                  value={form.days}
                  disabled={saving}
                  onChange={(e) => set({ days: e.target.value })}
                />
              </div>
            ) : null}

            <div className="dpFormGroup">
              <label htmlFor="leaveExpected">Previsão de retorno</label>
              <input
                id="leaveExpected"
                type="date"
                className="dpInput"
                value={form.expectedEndDate}
                disabled={saving}
                onChange={(e) => set({ expectedEndDate: e.target.value })}
              />
            </div>

            {type === "OUTRO" && !editing ? (
              <div className="dpFormGroup">
                <label htmlFor="leavePayer">Quem paga os dias</label>
                <select
                  id="leavePayer"
                  className="dpSelect"
                  value={form.payer}
                  disabled={saving}
                  onChange={(e) => set({ payer: e.target.value as DpLeavePayer })}
                >
                  {(Object.keys(LEAVE_PAYER_LABELS) as DpLeavePayer[]).map((p) => (
                    <option key={p} value={p}>
                      {LEAVE_PAYER_LABELS[p]}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {!editing && type && CHAINABLE_TYPES.includes(type) && chainCandidates.length > 0 ? (
              <div className="dpFormGroup">
                <label htmlFor="leaveParent">Continuação de um afastamento anterior</label>
                <select
                  id="leaveParent"
                  className="dpSelect"
                  value={form.parentLeaveId}
                  disabled={saving}
                  onChange={(e) => set({ parentLeaveId: e.target.value })}
                >
                  <option value="">Não — é um afastamento novo</option>
                  {chainCandidates.map((l) => (
                    <option key={l.id} value={l.id}>
                      {LEAVE_TYPE_LABELS[l.type]} de {formatDateOnly(l.startDate)} ({l.employerPaidDays} dia(s) pagos pela empresa)
                    </option>
                  ))}
                </select>
                <span className="dpFormHint">
                  Mesma doença em até 60 dias: a empresa só paga o que faltar para completar 15 dias. A decisão é do DP.
                </span>
              </div>
            ) : null}
          </div>

          {type === "ACIDENTE_TRABALHO" ? (
            <div className="dpFormGrid">
              <div className="dpFormGroup">
                <label htmlFor="catNumber">Número da CAT</label>
                <input
                  id="catNumber"
                  className="dpInput"
                  maxLength={30}
                  value={form.catNumber}
                  disabled={saving}
                  onChange={(e) => set({ catNumber: e.target.value })}
                />
              </div>
              <div className="dpFormGroup">
                <label htmlFor="catIssued">Emissão da CAT</label>
                <input
                  id="catIssued"
                  type="date"
                  className="dpInput"
                  value={form.catIssuedAt}
                  disabled={saving}
                  onChange={(e) => set({ catIssuedAt: e.target.value })}
                />
              </div>
            </div>
          ) : null}

          {isDp && (isSickness || type === "AUXILIO_DOENCA_INSS" || type === "LICENCA_MATERNIDADE") ? (
            <div className="dpSensitiveBlock">
              <strong>Dados de saúde (só o DP vê)</strong>
              <span className="dpFormHint" style={{ marginTop: -8 }}>
                LGPD art. 11. O CID é opcional — o colaborador pode se recusar a informar.
                {editing ? " Campos vazios não alteram o que já está gravado." : ""}
              </span>
              <div className="dpFormGrid">
                <div className="dpFormGroup">
                  <label htmlFor="leaveCid">CID</label>
                  <input
                    id="leaveCid"
                    className="dpInput"
                    maxLength={10}
                    value={form.cid}
                    disabled={saving}
                    onChange={(e) => set({ cid: e.target.value })}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="leavePhysician">Médico</label>
                  <input
                    id="leavePhysician"
                    className="dpInput"
                    maxLength={200}
                    value={form.physicianName}
                    disabled={saving}
                    onChange={(e) => set({ physicianName: e.target.value })}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="leaveCrm">CRM</label>
                  <input
                    id="leaveCrm"
                    className="dpInput"
                    maxLength={20}
                    value={form.physicianCrm}
                    disabled={saving}
                    onChange={(e) => set({ physicianCrm: e.target.value })}
                  />
                </div>
              </div>
              <div className="dpFormGrid">
                <FileField
                  id="leaveAttachment"
                  label="Atestado / laudo (PDF ou imagem, até 10 MB)"
                  file={attachment}
                  onChange={setAttachment}
                  disabled={saving}
                  hint={editing ? "Enviar outro substitui o atual." : "Opcional."}
                />
                {type === "ACIDENTE_TRABALHO" ? (
                  <FileField
                    id="leaveCat"
                    label="CAT (PDF ou imagem, até 10 MB)"
                    file={cat}
                    onChange={setCat}
                    disabled={saving}
                    hint="Opcional."
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {!isDp && isSickness ? (
            <p className="dpNotice">CID, médico e atestado são registrados pelo papel DP (dado de saúde).</p>
          ) : null}

          <div className="dpFormGroup">
            <label htmlFor="leaveNotes">Observações (administrativas — não registre diagnóstico)</label>
            <textarea
              id="leaveNotes"
              className="dpTextarea"
              maxLength={1000}
              value={form.notes}
              disabled={saving}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </div>

          <RuleMessages errors={errors} />
        </div>

        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={submit} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </footer>
      </div>
    </div>
  );
}
